import { inject, Injectable, signal } from "@angular/core";
import { DefaultService } from "@core/services/default.service";
import {
  ISystemConfiguration,
  ISystemConfigurationRenderData,
} from "@modules/management/models/system-configurations.interface";
import { catchError, tap, throwError } from "rxjs";
import { finalize } from "rxjs/operators";
import { FormControl, FormGroup } from "@angular/forms";
import { AuthService } from "@modules/auth";

@Injectable({
  providedIn: "root",
})
export class SystemConfigurationsService extends DefaultService<
  ISystemConfiguration,
  ISystemConfiguration,
  ISystemConfiguration
> {
  _authService = inject(AuthService);
  systemConfigForm: FormGroup;

  // STATE
  isSystemConfigurationsFetching = signal(false);
  isSystemConfigurationsUpdating = signal(false);
  isShownSystemConfigurationsRenderForm = signal(false);

  currentDate = new Date();

  systemConfigurationCourtWarningTemplate: ISystemConfigurationRenderData[] = [
    {
      key: "courtWarningToCourtProcessDayInterval",
      title:
        "MANAGEMENT.SYSTEM_CONFIGURATIONS.COURT_WARNING_TO_COURT_PROCESS_DAY_INTERVAL",
      icon: "calendar",
      value: 0,
    },
    {
      key: "courtWarningValidationMonthInterval",
      title:
        "MANAGEMENT.SYSTEM_CONFIGURATIONS.COURT_WARNING_VALIDATION_MONTH_INTERVAL",
      icon: "calendar",
      value: 0,
    },
    {
      key: "debtPartialCoveragePercentage",
      title:
        "MANAGEMENT.SYSTEM_CONFIGURATIONS.COURT_WARNING_DEBT_PARTIAL_COVERAGE_PERCENTAGE",
      icon: "percentage",
      value: 0,
    },
    {
      key: "stateDutyAmount",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.STATE_DUTY_AMOUNT",
      icon: "tag",
      value: 0,
    },
    {
      key: "postFeeAmount",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.POST_FREE_AMOUNT",
      icon: "cheque",
      value: 0,
    },
    {
      key: "maxCourtWarningCreateLimit",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.MAX_COURT_WARNING_CREATE_LIMIT",
      icon: "note-2",
      value: 0,
    },
    {
      key: "qqsPercentage",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.QQS_PERCENTAGE",
      icon: "percentage",
      value: 0,
    },
    {
      key: "minimumMahallaServiceabilityIndex",
      title:
        "MANAGEMENT.SYSTEM_CONFIGURATIONS.MINIMUM_MAHALLA_SERVICE_ABILITY_INDEX",
      icon: "burger-menu-2",
      value: 0,
    },
    {
      key: "minimumCHYMTimeInMinutes",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.MINIMUM_CHYM_TIME_IN_MINUTES",
      icon: "time",
      value: 0,
    },
    {
      key: "wasteCollectionAreaRadius",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.WASTE_COLLECTION_AREA_RADIUS",
      icon: "focus",
      value: 0,
    },
    {
      key: "qqsStartAt",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.QSS_STARTS_AT",
      icon: "calendar",
      value: 0,
    },
    {
      key: "publicFileDownloadUrlPrefix",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.PUBLIC_FILE_DOWNLOAD_URL_PREFIX",
      icon: "file",
      value: 0,
    },
    {
      key: "affectCHYMVisitToBalance",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.AFFECT_CHYM_VISIT_TO_BALANCE",
      icon: "toggle-off-circle",
      value: false,
    },
    {
      key: "affectMahallaVisitToBalance",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.AFFECT_MAHALLA_VISIT_TO_BALANCE",
      icon: "toggle-off-circle",
      value: false,
    },

    {
      key: "gpsWeightDuration",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.GPS_WEIGHT_DURATION",
      icon: "percentage",
      value: false,
    },

    {
      key: "gpsWeightMileage",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.GPS_WEIGHT_MILEAGE",
      icon: "percentage",
      value: false,
    },

    {
      key: "gpsWeightStops",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.GPS_WEIGHT_STOPS",
      icon: "percentage",
      value: false,
    },

    {
      key: "gpsWeightSpeed",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.GPS_WEIGHT_SPEED",
      icon: "percentage",
      value: false,
    },

    {
      key: "logOutOnError",
      title: "Logout checker",
      icon: "exit-right",
      value: false,
    },

    {
      key: "lastMiaSynchronizationMonth",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.LAST_MIA_SYNC_MONTH",
      icon: "calendar",
      value: 0,
    },

    {
      key: "residentCronOpenHour",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.RESIDENT_CRON_OPEN_HOUR",
      icon: "time",
      value: null,
    },

    {
      key: "residentCronCloseHour",
      title: "MANAGEMENT.SYSTEM_CONFIGURATIONS.RESIDENT_CRON_CLOSE_HOUR",
      icon: "time",
      value: null,
    },
  ];

  // GETTERS & SETTERS

  get getSystemConfigurationCourtWarningTemplate() {
    return this.systemConfigurationCourtWarningTemplate;
  }

  constructor() {
    super("user-service/system-configurations");
  }

  // METHODS
  getServiceKey(): string {
    return "SystemConfigurationsService";
  }

  checkPermissionForLogout() {
    return this.getAll<ISystemConfiguration>().pipe(
      catchError((error) => {
        this._authService.logout();
        return throwError(() => error);
      }),
      tap((res) => {
        if (res.logOutOnError) this._authService.logout();
      })
    );
  }

  getCourtWarningConfigurations() {
    this.isSystemConfigurationsFetching.set(true);

    this.getAll<ISystemConfiguration>()
      .pipe(
        tap((systemConfigurationResponse) => {
          this.setSystemConfigurationCourtWarningTemplate(
            systemConfigurationResponse
          );
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
        finalize(() => {
          this.isSystemConfigurationsFetching.set(false);
          this.setToForm();
        })
      )
      .subscribe();
  }

  setSystemConfigurationCourtWarningTemplate(
    responseOfISystemConfigurations: ISystemConfiguration
  ) {
    this.getSystemConfigurationCourtWarningTemplate.forEach(
      (systemConfiguration) => {
        if (responseOfISystemConfigurations[systemConfiguration.key]) {
          systemConfiguration.value =
            responseOfISystemConfigurations[systemConfiguration.key];
        }
      }
    );
  }

  setToForm() {
    this.systemConfigForm = new FormGroup({});
    this.getSystemConfigurationCourtWarningTemplate.forEach(
      (systemConfiguration) => {
        this.systemConfigForm.addControl(
          systemConfiguration.key,
          new FormControl(systemConfiguration.value)
        );
      }
    );
  }

  submitForm() {
    this.isSystemConfigurationsUpdating.set(true);
    this._http
      .put(this._api, { ...this.systemConfigForm.value }, {})
      .pipe(
        tap(() => {
          this._toastService.success("TOAST.TOAST_UPDATE");
          this.getCourtWarningConfigurations();
        }),
        catchError((err) => {
          this._toastService.error(`
            ${err.error.code}
            ${err.error.message}
          `);
          return throwError(() => err);
        }),
        finalize(() => {
          this._modalService.close();
          this.isSystemConfigurationsUpdating.set(false);
          this.isShownSystemConfigurationsRenderForm.set(false);
        })
      )
      .subscribe();
  }
}
